import React, { Children, cloneElement, forwardRef } from "react";
import styled from "styled-components";
import {
  FontSize,
  FontWeight,
  Spacing,
  background,
  content,
  ellipsis,
  prettyScrollbars,
} from "@glossy";
import Button from "@ui-kit/button";
import { isFullscreenScrollableDataviz } from "@radarly/models/cards/dataviz";

interface FullscreenProps {
  children: React.ReactElement;
  isFullscreen: boolean;
  title: string;
  closeFullscreen: (value: boolean) => void;
  subtitle?: React.ReactNode;
  magicFooter?: React.ReactNode;
}

const Root = styled.div`
  color: ${content};
  background-color: ${background};
`;

const Content = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${Spacing.Large};
  row-gap: ${Spacing.ExtraLarge};
`;

const Header = styled.div`
  display: flex;
`;

const HeaderInfos = styled.div`
  margin-left: ${Spacing.Medium};
`;

const Title = styled.div`
  font-weight: ${FontWeight.Bold};
  color: ${content};
  font-size: ${FontSize.ExtraLarge};
  text-transform: uppercase;
`;

const Inner = styled.div<{ isScrollable: boolean }>`
  ${(props) => (props.isScrollable ? prettyScrollbars() : "overflow: hidden;")}
  flex-grow: 1;
`;

const Subtitle = styled.div`
  font-size: ${FontSize.Medium};
  color: ${(props) => content(props, "secondary")};
  margin-top: ${Spacing.ExtraSmall};
  width: 100%;
  ${ellipsis()};
`;


const Fullscreen = forwardRef(
  (
    {
      children,
      isFullscreen,
      title,
      closeFullscreen,
      subtitle,
      magicFooter,
    }: FullscreenProps,
    ref: React.MutableRefObject<HTMLDivElement>
  ) => {
    const isScrollable = isFullscreenScrollableDataviz(
      children?.props?.dataviz
    );
    return (
      <Root ref={ref}>
        {isFullscreen && (
          <Content>
            <Header>
              <div style={{ marginTop: "-" + Spacing.Micro }}>
                <Button
                  icon="arrow-left"
                  variant="tertiary"
                  size="l"
                  onClick={closeFullscreen}
                />
              </div>
              <HeaderInfos>
                <Title>{title}</Title>
                <Subtitle>{subtitle}</Subtitle>
              </HeaderInfos>
            </Header>
            <Inner isScrollable={isScrollable}>
              {Children.map(children, (child) =>
                cloneElement(child, { isFullscreen: true, closeFullscreen })
              )}
            </Inner>
            {magicFooter}
          </Content>
        )}
      </Root>
    );
  }
);

export default Fullscreen;
